/**
 * What the §F6 forms are allowed to offer — the §D5 catalogue, filtered per Part H.
 *
 * One module rather than a filter per form, because every one of these predicates is a
 * *refusal the backend would make anyway*, and a picker that offers a layer the tool
 * refuses turns a form into a failed job the reader cannot act on:
 *
 * * **`has_data`** (§D5, M4 Task 1) — a registered layer with no published version behind
 *   it has no COG for `rasters.layer_cog` to open and no rows for `vector.layer_table`;
 * * **`access !== 'none'`** — §I3 requires ≥LIMITED on every input, so a NONE row is a 403
 *   before the tool runs. (The server already omits NONE rows for public/field/partner, so
 *   this only bites analyst/manager, which is exactly when it must.)
 * * **`cog_titiler` only** for a raster: a `wms_external` layer is `display_only` (§1a rule
 *   4) — the pixels are never stored here, so there is nothing to run statistics over;
 * * **`is_categorical`** — §H3 refuses a class raster (T4), §H5 requires one;
 * * **`wpdens`** — its "features" are org units and it has no geometry column of its own
 *   (`features.FeatureSource.geom_column is None`), so §H7 and §H8 refuse it by name.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchLayers, queryKeys, type Layer } from '../../lib/api';
import { useAppStore } from '../../lib/state';

/** The §D5 catalogue for the current role, as every pane reads it. */
export function useAnalysisLayers(): Layer[] {
  const role = useAppStore((s) => s.role);
  const { data } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  return data ?? [];
}

const usable = (layer: Layer): boolean =>
  !layer.unavailable && layer.access !== 'none' && layer.has_data !== false;

/** Rasters an analysis tool can actually open — see the module header. */
export const analysisRasters = (layers: readonly Layer[]): Layer[] =>
  layers.filter((l) => l.kind === 'cog_titiler' && usable(l));

/** §H3's picker: the same list without the class rasters the tool refuses. */
export const continuousRasters = (layers: readonly Layer[]): Layer[] =>
  analysisRasters(layers).filter((l) => !l.is_categorical);

/**
 * §H5's two epoch selects. **This is the whole of the epoch catalogue** — there is no
 * `/analysis/epochs` route, deliberately: §D5 already carries `epoch` and is already
 * access-filtered, and a second list could disagree with the first (T6).
 */
export const epochRasters = (layers: readonly Layer[]): Layer[] =>
  analysisRasters(layers)
    .filter((l) => l.is_categorical && typeof l.epoch === 'number')
    .sort((a, b) => (a.epoch ?? 0) - (b.epoch ?? 0));

/** Layers with geometry of their own — §H7's input and §H8's A side. */
export const VECTOR_KINDS: Layer['kind'][] = ['points_api', 'lines_api', 'polygons_api'];

/** The one vector row whose features are org units rather than its own geometry. */
export const NO_GEOMETRY_LAYERS = ['wpdens'];

export const analysisVectors = (layers: readonly Layer[]): Layer[] =>
  layers.filter(
    (l) => VECTOR_KINDS.includes(l.kind) && usable(l) && !NO_GEOMETRY_LAYERS.includes(l.id),
  );

/** §H7's default input — Part H names it. */
export const DEFAULT_BUFFER_LAYER = 'wpts';

/** §H8's default A side — the mockup's, and the only polygon feed with programme cover. */
export const DEFAULT_OVERLAY_LAYER = 'settle';

/** §H8's layer-B raster-mask presets (`tools/overlay.PRESETS`), in the tool's own order. */
export const OVERLAY_PRESETS = ['erosion_high', 'cropland'] as const;

export type OverlayPreset = (typeof OVERLAY_PRESETS)[number];

/**
 * Pick a starting value for a select: the caller's preference if it is still on offer,
 * else the first option, else the empty string. Written once because five forms need it
 * and a select left pointing at a layer that has gone is a form that submits an id the
 * catalogue no longer has.
 */
export function firstAvailable(
  options: readonly Layer[],
  ...preferred: (string | null | undefined)[]
): string {
  for (const id of preferred) {
    if (id && options.some((l) => l.id === id)) return id;
  }
  return options[0]?.id ?? '';
}
